'use client';

import { ChevronDown, FileText, Home, LogOut } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { signOut, useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import type { Session } from 'next-auth';
import { Button } from '@/components/ui/button';
import { useScrollPosition } from '@/hooks/useScrollPosition';

interface UserMenuProps {
  user: Session['user'];
  /** True on light backgrounds, where the trigger needs dark text. */
  useDarkText: boolean;
}

/** Avatar dropdown for a signed-in visitor: dashboard, terms and logout. */
function UserMenu({ user, useDarkText }: UserMenuProps) {
  const handleLogout = async () => {
    await signOut({
      callbackUrl: '/',
      redirect: true,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`flex items-center gap-2 rounded-full pl-2 pr-4 py-1 transition-all ${
            useDarkText ? 'hover:bg-slate-100 text-slate-700' : 'hover:bg-white/10 text-white'
          }`}
        >
          <Avatar className={`h-8 w-8 border ${useDarkText ? 'border-slate-200' : 'border-white/20'}`}>
            <AvatarImage src={user.imageUrl} alt={user.name} />
            <AvatarFallback className="bg-slate-900 text-white">{user.name[0]}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium hidden sm:block font-sans">
            {user.name?.split(' ')[0]}
          </span>
          <ChevronDown className={`h-4 w-4 ${useDarkText ? 'text-slate-400' : 'text-white/60'}`} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 mt-1 p-2">
        <div className="px-2 py-1.5 mb-2">
          <p className="text-sm font-medium text-gray-900">{user.name}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <Link href="/dashboard" prefetch={false} className="w-full">
            <DropdownMenuItem className="cursor-pointer flex gap-2 items-center py-2">
              <Home className="h-4 w-4" />
              Dasbor
            </DropdownMenuItem>
          </Link>
          <Link href="/terms" prefetch={false} className="w-full">
            <DropdownMenuItem className="cursor-pointer flex gap-2 items-center py-2">
              <FileText className="h-4 w-4" />
              Syarat & Ketentuan
            </DropdownMenuItem>
          </Link>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer flex gap-2 items-center py-2 text-red-500 hover:!text-red-600 hover:!bg-red-50"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          Keluar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface NavbarProps {
  initialVariant?: 'light' | 'dark';
}

export default function Navbar({ initialVariant = 'light' }: NavbarProps) {
  const session = useSession();
  const [isMounted, setIsMounted] = useState(false);
  const scrollPosition = useScrollPosition();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isAuthenticated = isMounted && !!session.data?.user;

  // Determine if navbar should have dark theme (when scrolled or on light backgrounds)
  const isScrolled = scrollPosition > 50;

  // Colors based on variant and scroll state
  // If scrolled, always white bg with dark text
  // If not scrolled and variant is dark (e.g. night sky bg), use white text
  // If not scrolled and variant is light (e.g. white bg), use dark text
  const useDarkText = isScrolled || initialVariant === 'light';

  const textColor = useDarkText
    ? 'text-slate-600 hover:text-slate-900'
    : 'text-white/80 hover:text-white';
  const logoSrc = useDarkText ? '/static/images/logo-black.png' : '/static/images/logo-white.png';

  // Handle demo button click
  const handleDemoClick = () => {
    window.open('https://wa.me/6282313436274', '_blank');
  };

  return (
    <header
      className={`fixed top-[calc(var(--top-bar-height,0px)+var(--maintenance-bar-height,0px))] z-40 w-full transition-all duration-500 py-4 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-md shadow-sm border-b border-slate-200/50'
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between px-4 lg:px-20 xl:px-40 w-full">
        <Link href="/" className="flex items-center gap-2 transition-all" prefetch={false}>
          <Image
            src={logoSrc}
            alt="Justisio"
            width={130}
            height={36}
            quality={100}
            sizes="130px"
            priority
            className="transition-all duration-500"
          />
        </Link>

        <nav className="hidden items-center gap-8 md:flex absolute left-1/2 transform -translate-x-1/2">
          <Link
            href="/legal-tasks/action/search-indonesia-regulation"
            className={`text-sm flex gap-2 items-center font-medium transition-colors font-sans ${textColor}`}
          >
            Tugas
          </Link>
          <Link
            href="/blog"
            className={`text-sm flex gap-2 items-center font-medium transition-colors font-sans ${textColor}`}
          >
            Kajian
          </Link>
          <Link
            href="/about"
            className={`text-sm flex gap-2 items-center font-medium transition-colors font-sans ${textColor}`}
          >
            Tentang Kami
          </Link>
        </nav>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-4 md:flex ml-auto">
          {/* CTA Buttons */}
          <div className="flex items-center gap-3">
            {/* if user is not logged in, show login button */}
            {!isAuthenticated && (
              <Link href="/login" prefetch={false}>
                <Button
                  className={`px-6 rounded-full transition-all font-sans shadow-md hover:shadow-lg ${
                    useDarkText
                      ? 'bg-slate-900 text-white hover:bg-slate-800'
                      : 'bg-white text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  Masuk
                </Button>
              </Link>
            )}

            {/* if user is logged in, show user profile */}
            {isAuthenticated && session.data?.user && (
              <UserMenu user={session.data.user} useDarkText={useDarkText} />
            )}
          </div>

          {/* Demo Button - appears when scrolled */}
          {!isAuthenticated && (
            <Button
              onClick={handleDemoClick}
              className="px-6 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 font-sans"
            >
              Demo & Konsultasi
            </Button>
          )}
        </nav>

        {/* Mobile Navbar */}
        <div className="flex items-center gap-4 md:hidden">
          {/* Mobile Demo Button */}
          <Button
            onClick={handleDemoClick}
            size="sm"
            className="px-4 rounded-full bg-slate-900 text-white hover:bg-slate-800 text-xs font-sans"
          >
            Demo
          </Button>
        </div>
      </div>
    </header>
  );
}
